import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePromo } from './PromoContext';
import { apiService } from '../services/api';
import { ORDER_STATUS } from '../constants';
import { orderDisplayNumber } from '../utils/orderDisplay';

const STORAGE_KEY = 'qc_notif_dismissed';

const ACTIVE_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.AWAITING_CONFIRMATION,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.PAID,
  ORDER_STATUS.PREPARING,
  ORDER_STATUS.READY,
];

const STATUS_INFO = {
  [ORDER_STATUS.PENDING]: { icon: '⏳', text: 'Ожидает обработки' },
  [ORDER_STATUS.AWAITING_CONFIRMATION]: { icon: '⏳', text: 'Ожидает обработки' },
  [ORDER_STATUS.CONFIRMED]: { icon: '✅', text: 'Подтверждён' },
  [ORDER_STATUS.PAID]: { icon: '💳', text: 'Оплачен' },
  [ORDER_STATUS.PREPARING]: { icon: '☕', text: 'Готовится' },
  [ORDER_STATUS.READY]: { icon: '🎉', text: 'Готов к выдаче!' },
};

const NotificationContext = createContext(null);

function readDismissed() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { activePromoCode, appliedPromoCode } = usePromo();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeOrder, setActiveOrder] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(readDismissed);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchActiveOrders = async () => {
      if (!user) {
        if (!cancelled) setActiveOrder(null);
        return;
      }
      try {
        const orders = await apiService.getUserOrders(null);
        const active = orders?.find((order) => ACTIVE_STATUSES.includes(order.status));
        if (!cancelled) setActiveOrder(active || null);
      } catch (err) {
        console.error('Error fetching active orders:', err);
        if (!cancelled) setActiveOrder(null);
      }
    };

    fetchActiveOrders();
    const interval = setInterval(fetchActiveOrders, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  const dismiss = useCallback((id) => {
    setDismissedIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeDismissed(next);
      return next;
    });
  }, []);

  const notifications = useMemo(() => {
    const items = [];

    if (activeOrder) {
      const id = `order-${activeOrder.id}`;
      if (!dismissedIds.includes(id)) {
        const info = STATUS_INFO[activeOrder.status] || { icon: '📦', text: 'Активный заказ' };
        items.push({
          id,
          type: 'order',
          icon: info.icon,
          title: 'Активный заказ',
          subtitle: `${info.text} • #${orderDisplayNumber(activeOrder)}`,
          onActivate: () => {
            setPanelOpen(false);
            navigate(`/order/${activeOrder.id}`);
          },
        });
      }
    }

    const onCart = location.pathname === '/cart';
    const promoApplied = appliedPromoCode && appliedPromoCode === activePromoCode;
    if (activePromoCode && !promoApplied && !onCart) {
      const id = `promo-${activePromoCode}`;
      if (!dismissedIds.includes(id)) {
        items.push({
          id,
          type: 'promo',
          icon: '🏷️',
          title: 'Промокод',
          subtitle: activePromoCode,
          onActivate: async () => {
            try {
              await navigator.clipboard.writeText(activePromoCode);
            } catch (err) {
              console.error('Copy failed:', err);
            }
            dismiss(id);
            setPanelOpen(false);
          },
        });
      }
    }

    return items;
  }, [
    activeOrder,
    activePromoCode,
    appliedPromoCode,
    dismissedIds,
    location.pathname,
    navigate,
    dismiss,
  ]);

  const value = useMemo(
    () => ({
      notifications,
      count: notifications.length,
      panelOpen,
      setPanelOpen,
      togglePanel: () => setPanelOpen((v) => !v),
      closePanel: () => setPanelOpen(false),
      dismiss,
    }),
    [notifications, panelOpen, dismiss],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
